import { useCallback, useEffect, useRef, useState } from "react";
import {
    ecdbIdentityResponseInit,
    idDataSetsFilterInit,
    IIDDataSet,
    IIDDataSetFilter,
} from "../../models/crewMember";
import { SearchPageResults } from "./SearchPageResults";
import { CSSTransition } from "react-transition-group";
import { SearchPageFilters } from "./SearchPageFilters";
import { Button } from "semantic-ui-react";
import { CrewMemberFormModal } from "../CrewMember/CrewMemberFormModal";
import { useIntl } from "react-intl";
import { toast } from "../..";
import { AppState } from "../../store/configureStore";
import { useSelector } from "react-redux";
import { UserRole } from "../../actions/authentificationActions";
import {
    getQueryIDDataSets,
    sendCrewIdentityRequestToECDB,
    getCrewIdentityResponses,
} from "../../api/crewMembers";
import { useLocation } from "react-router-dom";
import "./SearchPage.css";

export const SearchPage = () => {
    const loc = useLocation();
    const user = useSelector((state: AppState) => state.user);
    const intl = useIntl();
    const [queryFilter, setQueryFilter] = useState(idDataSetsFilterInit);
    const [idDataSets, setIdDataSets] = useState<IIDDataSet[]>([]);
    const [ecdbResponse, setEcdbResponse] = useState(ecdbIdentityResponseInit);
    const [modalOpen, setModalOpen] = useState(false);
    const [identityRequestId, setIdentityRequestId] = useState("");
    const [detailRequestId, setDetailRequestId] = useState("");
    const [loaded, setLoaded] = useState(false);
    const currentRef = useRef<string[]>([]);

    const fetchCrewMembers = useCallback(
        async (filter: IIDDataSetFilter, searchByCid: boolean) => {
            if (
                !searchByCid &&
                filter.firstName === "" &&
                filter.lastName === "" &&
                filter.dateOfBirth === "-1/-1/-1" &&
                filter.placeOfBirth === ""
            ) {
                return;
            }
            setIdDataSets([]);
            setEcdbResponse(ecdbIdentityResponseInit);
            setLoaded(false);
            currentRef.current = [];
            try {
                const res = await getQueryIDDataSets(filter, searchByCid);
                setIdDataSets(res);
                setLoaded(true);
                const ids = await sendCrewIdentityRequestToECDB(filter, searchByCid);
                setIdentityRequestId(ids.identityRequestId.replace(/"/g, "").replace(/-/g, ""));
                setDetailRequestId(ids.detailRequestId.replace(/"/g, "").replace(/-/g, ""));
            } catch {
                toast(intl.formatMessage({ id: "unableToFetchDataSets" }), false);
            }
        },
        [intl],
    );

    const fetchResponsesFromECDB = useCallback(async () => {
        try {
            const res = await getCrewIdentityResponses(identityRequestId, currentRef.current);
            setEcdbResponse(res);
            currentRef.current = res.current;

            setIdDataSets(prev => {
                // Create a Map of existing items using a composite key (CID + Authority)
                // This ensures that even if GUIDs change, the person remains unique.
                const existingMap = new Map();
                const noCidItems: IIDDataSet[] = []; // Track items without CID separately

                prev.forEach(item => {
                    if (item.cid && item.cid.trim() !== "") {
                        existingMap.set(`${item.cid}-${item.issuingAuthority}`, item);
                    } else {
                        noCidItems.push(item); // Keep all items without CID
                    }
                });

                // Add/Overwrite with items from the new response
                res.dataSets.forEach(newItem => {
                    if (newItem.cid && newItem.cid.trim() !== "") {
                        existingMap.set(`${newItem.cid}-${newItem.issuingAuthority}`, newItem);
                    } else {
                        noCidItems.push(newItem); // Keep all items without CID
                    }
                });

                // Convert back to array, combine with no-CID items, and sort
                const itemsWithCid = Array.from(existingMap.values()).sort((a, b) =>
                    a.cid.localeCompare(b.cid),
                );
                return [...itemsWithCid, ...noCidItems];
            });
        } catch (error) {
            console.error("ECDB Polling error:", error);
        }
    }, [identityRequestId]);

    useEffect(() => {
        if (!!identityRequestId) {
            fetchResponsesFromECDB();
            let count = 1;
            const intervalId = setInterval(() => {
                if (count >= 24) setIdentityRequestId("");
                fetchResponsesFromECDB();
                count++;
            }, 5000);
            return () => clearInterval(intervalId);
        }
    }, [identityRequestId, fetchResponsesFromECDB]);

    useEffect(() => {
        if (loc.state && typeof loc.state === "string") {
            const qrCid = loc.state as string;
            const newFilter: IIDDataSetFilter = { ...idDataSetsFilterInit, cid: qrCid };
            setQueryFilter(newFilter);
            fetchCrewMembers(newFilter, true);
        }
    }, [fetchCrewMembers, loc.state]);

    return (
        <div className="search-page-container">
            <div className="container-header">
                <h1>{intl.formatMessage({ id: "searchCrewMember" })}</h1>
                {user.role !== UserRole.EnforcementAuthorityUser && (
                    <Button
                        primary
                        icon="plus"
                        content={intl.formatMessage({ id: "addCrewMember" })}
                        onClick={() => setModalOpen(true)}
                    />
                )}
            </div>
            <div className="search-container">
                <SearchPageFilters
                    queryFilter={queryFilter}
                    setQueryFilter={setQueryFilter}
                    fetchCrewMembers={fetchCrewMembers}
                />
                <CSSTransition in={loaded} timeout={100} classNames="fade" unmountOnExit>
                    <SearchPageResults
                        idDataSets={idDataSets}
                        ecdbFetching={!!identityRequestId}
                        ecdbResponse={ecdbResponse}
                        detailRequestId={detailRequestId}
                    />
                </CSSTransition>
            </div>
            <CrewMemberFormModal open={modalOpen} closeModal={() => setModalOpen(false)} />
        </div>
    );
};
